import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { closestCenter, DndContext, DragEndEvent, DragOverEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import ViewColumn from '@mui/icons-material/ViewColumn'
import { createPortal } from 'react-dom'
import { ProfileTableHeaderRow } from './ProfileTableHeaderRow'
import { ProfileTableSettingsBar } from './ProfileTableSettingsBar'
import { useProfileTableLayout } from './useProfileTableLayout'
import { useProfileTableResize } from './useProfileTableResize'
import { useProfileTableHeaderStyle } from './useProfileTableHeaderStyle'
import { computeStickyOffset } from './stickyOffset'
import type { ProfileColumnDef, ProfileTableProps, RowDragHandleProps } from './types'
import { PROFILE_TABLE_VARIANT_STYLES } from './variantStyles'
import { TableSettings, SettingsColumn } from '../TableSettings'
import { TablePagination } from '../TablePagination'

// ==========================================
// SortableRow — internal, only used when `rowDnd` is enabled
// ==========================================
// Wraps a data <tr> in useSortable so the whole row lifts/animates, and hands
// the drag-handle props to `renderCells` so the caller places the grip on a
// cell (only the handle starts a drag — the row's onClick still fires).
interface SortableRowProps {
    id: string
    disabled: boolean
    className: string
    searchId?: string
    onClick: () => void
    renderCells: (dragHandle: RowDragHandleProps) => React.ReactNode
}

const SortableRow = ({ id, disabled, className, searchId, onClick, renderCells }: SortableRowProps) => {
    const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id, disabled })
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        // The lifted row is drawn by the DragOverlay ghost; hide the in-place one.
        opacity: isDragging ? 0 : 1,
    }
    const dragHandle: RowDragHandleProps = {
        ref: setActivatorNodeRef,
        attributes: attributes as unknown as Record<string, unknown>,
        listeners: listeners as unknown as Record<string, unknown> | undefined,
        isDragging,
    }
    return (
        <tr ref={setNodeRef} style={style} data-search-id={searchId} className={className} onClick={onClick}>
            {renderCells(dragHandle)}
        </tr>
    )
}

// Body-level DnD context wrapper — only mounts when row DnD is enabled, so
// non-DnD callers get zero extra context (behaviour identical to before).
interface RowDndProviderProps {
    enabled: boolean
    sensors: ReturnType<typeof useSensors>
    onDragStart: (event: DragStartEvent) => void
    onDragEnd: (event: DragEndEvent) => void
    onDragCancel: () => void
    overlay: React.ReactNode
    children: React.ReactNode
}

const RowDndProvider = ({ enabled, sensors, onDragStart, onDragEnd, onDragCancel, overlay, children }: RowDndProviderProps) => {
    if (!enabled) return <>{children}</>
    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel}>
            {children}
            <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>{overlay}</DragOverlay>
        </DndContext>
    )
}

const MaybeSortableContext = ({ enabled, items, children }: { enabled: boolean; items: string[]; children: React.ReactNode }) => {
    if (!enabled) return <>{children}</>
    return (
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
            {children}
        </SortableContext>
    )
}

export const ProfileTable = <TRow, TFixedKey extends string, TMiddleKey extends string>({
    data,
    fixedColumns,
    middleColumns,
    getRowKey,
    renderRow,
    getRowSearchId,
    selectedKeys,
    onSelectAll,
    totalSelectableCount,
    initialHiddenColumns,
    sortableColumns = [] as unknown as TMiddleKey[],
    activeSortColumn = null,
    sortDirection = 'asc',
    onSortChange,
    selectionSort,
    fixedColumnSorts,
    layoutStorageKey,
    deferDefaultLayoutPersistence,
    expandedKey,
    onToggleExpand,
    onRowClick,
    renderExpanded,
    showSettings: showSettingsProp = true,
    settingsControllerRef,
    settingsVariant: settingsVariantProp = 'modal',
    toolbar,
    columnsDropdownControllerRef,
    columnsDropdownAnchorRef,
    onColumnsDropdownVisibleCountChange,
    pagination,
    onPaginationChange,
    virtualizeRows,
    selectionColumnKey,
    loading = false,
    emptyMessage = 'No data found.',
    emptyState,
    uiVariant: uiVariantProp = 'default',
    tableClassName,
    className,
    rowDnd,
}: ProfileTableProps<TRow, TFixedKey, TMiddleKey>) => {
    const layout = useProfileTableLayout<TMiddleKey, TFixedKey>({
        middleColumns,
        fixedColumns,
        storageKey: layoutStorageKey,
        initialHiddenColumns,
        deferDefaultLayoutPersistence,
    })

    const {
        activeColumn,
        overColumn,
        setActiveColumn,
        setOverColumn,
        columnDefinitionMap,
        visibleMiddleColumns,
        visibleColumns,
        addableColumns,
        visibleHideableColumnsCount,
        getColumnWidth,
        isColumnCollapsed,
        toggleCollapse,
        hideColumn,
        addColumnToRight,
        reorderColumns,
        resizeColumn,
    } = layout

    const [settingsOpen, setSettingsOpen] = useState(false)
    const [columnsOpen, setColumnsOpen] = useState(false)
    const [pendingHiddenColumns, setPendingHiddenColumns] = useState<TMiddleKey[]>(layout.hiddenColumns)
    const columnsTriggerRef = useRef<HTMLDivElement | null>(null)
    const columnsMenuRef = useRef<HTMLDivElement | null>(null)
    const [columnsMenuPosition, setColumnsMenuPosition] = useState({ top: 0, left: 0 })

    useEffect(() => {
        if (!settingsControllerRef) return
        settingsControllerRef.current = {
            open: () => setSettingsOpen(true),
            close: () => setSettingsOpen(false),
        }
        return () => {
            settingsControllerRef.current = null
        }
    }, [settingsControllerRef])

    const settingsVariant: 'modal' | 'columnsDropdown' = settingsVariantProp
    const uiVariant = uiVariantProp
    const variantStyles = PROFILE_TABLE_VARIANT_STYLES[uiVariant]

    // The columns menu and the loading/empty states sit inside an otherwise-v2 table for the
    // trucks/trailers pages, so they need the semantic tokens too. Gated on uiVariant so the ~30
    // other ProfileTable consumers keep their existing look.
    const isV2 = uiVariant === 'v2'
    const mutedTextClass = isV2 ? 'text-foreground-muted' : 'text-slate-500 dark:text-slate-400'
    const columnsMenuClass = isV2
        ? 'fixed z-[9999] w-64 bg-surface rounded-xl shadow-overlay flex flex-col'
        : 'fixed z-[9999] w-64 bg-white dark:bg-[#1A1D1F] rounded-lg border border-gray-200 dark:border-[#404040] shadow-lg flex flex-col'
    const columnsItemHoverClass = isV2 ? 'hover:bg-surface-secondary' : 'hover:bg-gray-50 dark:hover:bg-[#2D3843]'
    const columnsItemTextClass = isV2 ? 'text-foreground' : 'text-gray-700 dark:text-gray-300'
    const columnsFooterClass = isV2 ? 'border-border' : 'border-gray-100 dark:border-[#2D3843]'

    const updateColumnsMenuPosition = useCallback(() => {
        const triggerEl = columnsDropdownAnchorRef?.current ?? columnsTriggerRef.current
        const menuEl = columnsMenuRef.current
        if (!triggerEl) return

        const rect = triggerEl.getBoundingClientRect()
        const menuHeight = menuEl?.offsetHeight ?? 360
        const menuWidth = menuEl?.offsetWidth ?? 256
        const spaceBelow = window.innerHeight - rect.bottom
        const openAbove = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8

        const top = openAbove ? rect.top - menuHeight - 4 : rect.bottom + 4
        const left = Math.max(8, rect.right - menuWidth)
        setColumnsMenuPosition({ top, left })
    }, [columnsDropdownAnchorRef])

    useEffect(() => {
        if (settingsVariant !== 'columnsDropdown') return
        setPendingHiddenColumns(layout.hiddenColumns)
    }, [layout.hiddenColumns, settingsVariant])

    useEffect(() => {
        if (settingsVariant !== 'columnsDropdown' || !columnsOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            const anchorEl = columnsDropdownAnchorRef?.current
            if (anchorEl?.contains(target) || columnsTriggerRef.current?.contains(target) || columnsMenuRef.current?.contains(target)) return
            setColumnsOpen(false)
        }
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setColumnsOpen(false)
        }
        const handleScroll = (e: Event) => {
            const target = e.target as Node | null
            // Allow scrolling inside the columns menu itself.
            if (target && columnsMenuRef.current?.contains(target)) return
            setColumnsOpen(false)
        }
        const handleResize = () => updateColumnsMenuPosition()
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', handleResize)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
            window.removeEventListener('scroll', handleScroll, true)
            window.removeEventListener('resize', handleResize)
        }
    }, [columnsDropdownAnchorRef, columnsOpen, settingsVariant, updateColumnsMenuPosition])

    useEffect(() => {
        if (settingsVariant !== 'columnsDropdown' || !columnsDropdownControllerRef) return
        columnsDropdownControllerRef.current = {
            open: () => setColumnsOpen(true),
            close: () => setColumnsOpen(false),
            toggle: () => setColumnsOpen((prev) => !prev),
        }
        return () => {
            columnsDropdownControllerRef.current = null
        }
    }, [columnsDropdownControllerRef, settingsVariant])

    useEffect(() => {
        if (settingsVariant !== 'columnsDropdown' || !onColumnsDropdownVisibleCountChange) return
        onColumnsDropdownVisibleCountChange(layout.visibleMiddleColumns.length)
    }, [layout.visibleMiddleColumns.length, onColumnsDropdownVisibleCountChange, settingsVariant])

    useLayoutEffect(() => {
        if (settingsVariant !== 'columnsDropdown' || !columnsOpen) return
        updateColumnsMenuPosition()
        // Position can change once menu measures (checkbox list). Recompute after paint.
        const id = window.setTimeout(updateColumnsMenuPosition, 0)
        return () => window.clearTimeout(id)
    }, [columnsOpen, settingsVariant, updateColumnsMenuPosition])

    const lockedMiddleColumnKeys = useMemo(() => new Set(middleColumns.filter((col) => !col.canHide).map((col) => col.key as TMiddleKey)), [middleColumns])

    // Build settings columns from middle columns
    const settingsColumns: SettingsColumn[] = useMemo(
        () => middleColumns.map((col) => ({ id: col.key, label: col.label, locked: !col.canHide })),
        [middleColumns]
    )

    // Visible columns map for TableSettings
    const settingsVisibleMap = useMemo(() => {
        const map: Record<string, boolean> = {}
        for (const col of middleColumns) {
            map[col.key] = !layout.hiddenColumns?.includes(col.key as TMiddleKey)
        }
        return map
    }, [middleColumns, layout])

    // Column order for TableSettings
    const settingsColumnOrder = useMemo(() => layout.visibleMiddleColumns.map((c) => c.key), [layout.visibleMiddleColumns])

    const handleSettingsColumnChange = useCallback(
        (key: string) => {
            const isHidden = !settingsVisibleMap[key]
            if (isHidden) {
                const lastVisible = layout.visibleMiddleColumns[layout.visibleMiddleColumns.length - 1]
                if (lastVisible) {
                    layout.addColumnToRight(lastVisible.key as TMiddleKey, key as TMiddleKey)
                }
            } else {
                layout.hideColumn(key as TMiddleKey)
            }
        },
        [settingsVisibleMap, layout]
    )

    const handleSettingsOrderChange = useCallback(
        (newOrder: string[]) => {
            // `newOrder` is the visible-columns slice coming from the settings modal.
            // We preserve the positions of hidden columns by appending them at the end
            // of the full middleColumnOrder — their relative order is otherwise lost
            // since the modal only surfaces visible columns to the user.
            //
            // Using `handleColumnsModalSave` is a single atomic state update: the
            // previous implementation called `reorderColumns` N-1 times in a loop,
            // which batched unpredictably and often produced stale orders (the bug
            // the user reported: "settings bosilsa columnlarni ozgartirib bolmayabdi
            // joyini"). One atomic save ⇒ one render ⇒ predictable result.
            const visibleSet = new Set(newOrder)
            const hiddenTail = layout.middleColumnOrder.filter((key) => !visibleSet.has(key))
            const nextMiddleOrder = [...(newOrder as TMiddleKey[]), ...hiddenTail]
            layout.handleColumnsModalSave(nextMiddleOrder, layout.hiddenColumns)
        },
        [layout]
    )

    const applyHiddenColumns = useCallback(
        (nextHidden: TMiddleKey[]) => {
            const sanitizedHidden = nextHidden.filter((key) => !lockedMiddleColumnKeys.has(key))
            layout.handleColumnsModalSave(layout.middleColumnOrder, sanitizedHidden)
        },
        [layout, lockedMiddleColumnKeys]
    )

    const { resizingColumn, handleResizeStart } = useProfileTableResize<TMiddleKey>({
        getColumnWidth: getColumnWidth as (column: TMiddleKey) => number,
        columnDefinitionMap: columnDefinitionMap as Record<string, ProfileColumnDef<string>>,
        onResize: resizeColumn as (column: TMiddleKey, width: number) => void,
    })

    // Accent colours for drop indicators / hover — resolved at CSS time from the
    // `--ds-table-accent` / `--ds-table-accent-soft` custom properties set on the
    // container below. This replaces the previous `useDashboardTheme().isDark`
    // runtime branch so the DS table no longer imports from `pages/dashboard/*`
    // (see `calm-spinning-turing.md` Phase 1).
    const accentColor = 'var(--ds-table-accent)'
    const hoverAccent = 'var(--ds-table-accent-soft)'

    // DnD
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
    const sortableItems = useMemo(() => visibleMiddleColumns.map((col) => col.key), [visibleMiddleColumns])

    const clearDragState = useCallback(() => {
        setActiveColumn(null)
        setOverColumn(null)
    }, [setActiveColumn, setOverColumn])

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            const activeId = event.active.id as TMiddleKey
            setActiveColumn(activeId)
            setOverColumn(activeId)
        },
        [setActiveColumn, setOverColumn]
    )

    const handleDragOver = useCallback(
        (event: DragOverEvent) => {
            if (!event.over?.id) return
            setOverColumn(event.over.id as TMiddleKey)
        },
        [setOverColumn]
    )

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const activeId = event.active.id as TMiddleKey
            const overId = event.over?.id as TMiddleKey | undefined

            if (overId && activeId !== overId) {
                const activeIndex = visibleMiddleColumns.findIndex((col) => col.key === activeId)
                const overIndex = visibleMiddleColumns.findIndex((col) => col.key === overId)
                const side: 'left' | 'right' = activeIndex < overIndex ? 'right' : 'left'
                reorderColumns(activeId, overId, side)
            }
            clearDragState()
        },
        [clearDragState, reorderColumns, visibleMiddleColumns]
    )

    // Selection
    const selectableTotal = totalSelectableCount ?? data.length
    const allSelected = selectableTotal > 0 && (selectedKeys?.size ?? 0) >= selectableTotal
    const someSelected = (selectedKeys?.size ?? 0) > 0 && !allSelected

    // Header cell style builder (extracted for reuse and readability)
    const getHeaderCellStyle = useProfileTableHeaderStyle<TFixedKey, TMiddleKey>({
        activeColumn,
        overColumn,
        resizingColumn,
        visibleMiddleColumns,
        accentColor,
        forceWidth: Boolean(virtualizeRows),
        getColumnWidth,
        isColumnCollapsed,
    })

    /**
     * Body-cell sizing, kept deliberately in lockstep with `getHeaderCellStyle`
     * above: when a column is neither collapsed nor force-sized, the width is
     * expressed as `minWidth` only and `width` stays undefined. A `<td>` that
     * pins a width its `<th>` lacks makes `table-layout: auto` redistribute
     * widths table-wide, which visually shifts cell content out from under its
     * header (worst after a reorder/resize, when persisted widths differ from
     * the defaults). Exposed to `renderRow` as `getCellStyle` so call-sites
     * don't re-derive this and drift.
     */
    const getCellStyle = useCallback(
        (key: TFixedKey | TMiddleKey): React.CSSProperties => {
            const column = columnDefinitionMap[key]
            const collapsed = isColumnCollapsed(key)
            const pinned = collapsed || Boolean(virtualizeRows)
            const width = getColumnWidth(key)
            return {
                width: pinned ? width : undefined,
                minWidth: pinned ? width : column?.minWidth,
                textAlign: column?.align ?? 'left',
                padding: collapsed ? '8px 4px' : '8px 12px',
            }
        },
        [columnDefinitionMap, getColumnWidth, isColumnCollapsed, virtualizeRows]
    )

    // Active column for drag overlay
    const activeColumnItem = useMemo(
        () => (activeColumn ? (visibleMiddleColumns.find((col) => col.key === activeColumn) ?? null) : null),
        [activeColumn, visibleMiddleColumns]
    )

    const fixedColumnKeys = useMemo(() => new Set(fixedColumns.map((col) => col.key as string)), [fixedColumns])

    const typedVisibleColumns = visibleColumns as ProfileColumnDef<TFixedKey | TMiddleKey>[]

    // Shared by the header and `renderRow` so pinned header and body cells stop at the same offset.
    const getStickyOffset = useCallback(
        (key: TFixedKey | TMiddleKey) =>
            computeStickyOffset(typedVisibleColumns, fixedColumnKeys, getColumnWidth as (column: TFixedKey | TMiddleKey) => number, key),
        [fixedColumnKeys, getColumnWidth, typedVisibleColumns]
    )

    // `rowDnd` and `virtualizeRows` are mutually exclusive (sortable index math needs every row
    // mounted; see types.ts). Nothing enforces that at the type level, so warn in dev if a caller
    // sets both — with both on, rows render unsliced while the spacers still use scroll-window math.
    useEffect(() => {
        if (process.env.NODE_ENV !== 'production' && rowDnd && virtualizeRows) {
            console.warn('ProfileTable: `rowDnd` and `virtualizeRows` are mutually exclusive — row DnD wins and virtualization is ignored.')
        }
    }, [rowDnd, virtualizeRows])

    // ---- Row virtualization (opt-in via `virtualizeRows`) -------------------
    // We measure the existing scroll container (the div around <table>) instead
    // of introducing a new one — this preserves sticky header behaviour and
    // avoids a layout shift for non-virtualized callers. When disabled, the
    // window covers the whole dataset so the slice/spacer logic is a no-op.
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const [scrollTop, setScrollTop] = useState(0)
    const [viewportHeight, setViewportHeight] = useState(0)

    useEffect(() => {
        if (!virtualizeRows) return
        const el = scrollContainerRef.current
        if (!el) return

        // Initial measure — `clientHeight` is 0 until the element is in the
        // layout tree, but by the time the effect runs it has been committed.
        setViewportHeight(el.clientHeight)
        setScrollTop(el.scrollTop)

        const handleScroll = () => setScrollTop(el.scrollTop)
        el.addEventListener('scroll', handleScroll, { passive: true })

        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => setViewportHeight(el.clientHeight)) : null
        resizeObserver?.observe(el)

        return () => {
            el.removeEventListener('scroll', handleScroll)
            resizeObserver?.disconnect()
        }
    }, [virtualizeRows])

    const totalRowCount = data.length
    const virtualWindow = useMemo(() => {
        if (!virtualizeRows || viewportHeight === 0 || totalRowCount === 0) {
            return { startIndex: 0, endIndex: totalRowCount, paddingTop: 0, paddingBottom: 0 }
        }
        const rowHeight = virtualizeRows.rowHeight
        const overscan = virtualizeRows.overscan ?? 8
        const firstVisible = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
        const visibleCount = Math.ceil(viewportHeight / rowHeight) + overscan * 2
        const lastVisible = Math.min(totalRowCount, firstVisible + visibleCount)
        return {
            startIndex: firstVisible,
            endIndex: lastVisible,
            paddingTop: firstVisible * rowHeight,
            paddingBottom: Math.max(0, (totalRowCount - lastVisible) * rowHeight),
        }
    }, [virtualizeRows, viewportHeight, scrollTop, totalRowCount])

    // ---- Row drag-and-drop reordering (opt-in via `rowDnd`) ------------------
    // Body-level DnD, independent of the header's column DnD (disjoint targets:
    // <tr> vs <th>). Optimistic order held locally; reject in onReorder to roll
    // back. Sortable ids are the row keys; the caller places the grip handle.
    const rowSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
    const [rowOrder, setRowOrder] = useState<string[] | null>(null)
    const [activeRowId, setActiveRowId] = useState<string | null>(null)
    const [rowReordering, setRowReordering] = useState(false)

    const baseRowIds = useMemo(() => (rowDnd ? data.map((row) => rowDnd.getSortableId(row)).filter((id): id is string => id != null) : []), [data, rowDnd])
    // Reseed the optimistic order whenever the server-supplied ids change.
    const baseRowIdsKey = baseRowIds.join('|')
    useEffect(() => {
        setRowOrder(null)
    }, [baseRowIdsKey])
    const effectiveRowIds = rowOrder ?? baseRowIds

    const handleRowDragStart = useCallback(
        (event: DragStartEvent) => {
            if (rowDnd?.disabled || rowReordering) return
            setActiveRowId(String(event.active.id))
        },
        [rowDnd?.disabled, rowReordering]
    )

    const handleRowDragEnd = useCallback(
        (event: DragEndEvent) => {
            setActiveRowId(null)
            if (!rowDnd || rowDnd.disabled || rowReordering) return
            const activeId = String(event.active.id)
            const overId = event.over ? String(event.over.id) : null
            if (!overId || activeId === overId) return
            const previous = effectiveRowIds
            const oldIndex = previous.indexOf(activeId)
            const newIndex = previous.indexOf(overId)
            if (oldIndex < 0 || newIndex < 0) return
            const next = arrayMove(previous, oldIndex, newIndex)
            setRowOrder(next)
            setRowReordering(true)
            rowDnd
                .onReorder(next)
                .catch(() => setRowOrder(previous))
                .finally(() => setRowReordering(false))
        },
        [rowDnd, rowReordering, effectiveRowIds]
    )

    // When rowDnd is on, render rows in the optimistic order.
    const orderedData = useMemo(() => {
        if (!rowDnd) return data
        const byId = new Map<string, TRow>()
        data.forEach((row) => {
            const id = rowDnd.getSortableId(row)
            if (id != null) byId.set(id, row)
        })
        const ordered = effectiveRowIds.map((id) => byId.get(id)).filter((row): row is TRow => row != null)
        // Append any rows without a sortable id (e.g. sentinel totals/add rows) in original order.
        const orderedSet = new Set(effectiveRowIds)
        data.forEach((row) => {
            const id = rowDnd.getSortableId(row)
            if (id == null || !orderedSet.has(id)) ordered.push(row)
        })
        return ordered
    }, [rowDnd, data, effectiveRowIds])

    return (
        <div
            className={`w-full flex flex-col h-full [--ds-table-accent:#0b6bcb] [--ds-table-accent-soft:rgba(11,107,203,0.16)] dark:[--ds-table-accent:#5da4ff] dark:[--ds-table-accent-soft:rgba(93,164,255,0.18)] ${className ?? ''}`}
        >
            {/*
             * If the caller provides a `toolbar` render prop, render that in place of the
             * default `ProfileTableSettingsBar`. This lets pages like InvoicesPage combine
             * their selection bar (checkbox + "Loads" label + unread badge) with the
             * settings-gear trigger on a single row, saving ~40px of vertical space.
             */}
            {toolbar
                ? toolbar({
                      onOpenSettings: () => {
                          if (settingsVariant === 'columnsDropdown') {
                              setColumnsOpen(true)
                              return
                          }
                          setSettingsOpen(true)
                      },
                  })
                : showSettingsProp &&
                  (settingsVariant === 'columnsDropdown' ? (
                      <>
                          {!columnsDropdownAnchorRef && (
                              <div className="flex items-center justify-end px-1 py-1">
                                  <div ref={columnsTriggerRef} className="relative">
                                      <button
                                          type="button"
                                          onClick={() => setColumnsOpen((prev) => !prev)}
                                          className="flex items-center gap-1.5 h-[34px] px-3 text-sm font-medium rounded-lg border border-gray-300 dark:border-[#404040] bg-white dark:bg-[#1A1D1F] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#2D3843]"
                                          title="Columns"
                                      >
                                          <ViewColumn style={{ fontSize: 16 }} />
                                          Columns
                                          <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-blue-100 text-blue-700">
                                              {layout.visibleMiddleColumns.length}
                                          </span>
                                      </button>
                                  </div>
                              </div>
                          )}
                          {columnsOpen &&
                              typeof document !== 'undefined' &&
                              createPortal(
                                  <div
                                      ref={columnsMenuRef}
                                      className={columnsMenuClass}
                                      style={{ top: columnsMenuPosition.top, left: columnsMenuPosition.left, maxHeight: 360 }}
                                  >
                                      <div className="overflow-auto py-2 flex-1">
                                          {layout.middleColumnOrder.map((key) => {
                                              const def = middleColumns.find((c) => c.key === key)
                                              if (!def) return null
                                              const locked = lockedMiddleColumnKeys.has(key)
                                              const isHidden = pendingHiddenColumns.includes(key)
                                              const visibleHideableCount = middleColumns.filter(
                                                  (c) => c.canHide && !pendingHiddenColumns.includes(c.key as TMiddleKey)
                                              ).length
                                              const disableHide = !locked && !isHidden && def.canHide && visibleHideableCount <= 1
                                              return (
                                                  <label
                                                      key={String(def.key)}
                                                      className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${columnsItemHoverClass} ${
                                                          locked || disableHide ? 'opacity-50 cursor-not-allowed' : ''
                                                      }`}
                                                  >
                                                      <input
                                                          type="checkbox"
                                                          checked={locked || !isHidden}
                                                          disabled={locked || disableHide}
                                                          onChange={() => {
                                                              if (locked || disableHide) return
                                                              setPendingHiddenColumns((prev) =>
                                                                  prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                                                              )
                                                          }}
                                                          className="accent-blue-600"
                                                      />
                                                      <span className={columnsItemTextClass}>{def.label}</span>
                                                      {locked && (
                                                          <span className={`text-[10px] ml-auto ${isV2 ? 'text-foreground-muted' : 'text-gray-400'}`}>
                                                              Locked
                                                          </span>
                                                      )}
                                                  </label>
                                              )
                                          })}
                                      </div>
                                      <div className={`flex items-center justify-between px-3 py-2 border-t shrink-0 ${columnsFooterClass}`}>
                                          <button
                                              type="button"
                                              onClick={() => {
                                                  const defaults = initialHiddenColumns ?? []
                                                  setPendingHiddenColumns(defaults)
                                                  applyHiddenColumns(defaults)
                                                  setColumnsOpen(false)
                                              }}
                                              className={`text-xs hover:underline ${isV2 ? 'text-accent' : 'text-blue-600'}`}
                                          >
                                              Reset
                                          </button>
                                          <button
                                              type="button"
                                              onClick={() => {
                                                  applyHiddenColumns(pendingHiddenColumns)
                                                  setColumnsOpen(false)
                                              }}
                                              className={`px-3 py-1 text-xs font-medium ${
                                                  isV2
                                                      ? 'rounded-3xl bg-accent text-accent-foreground hover:bg-accent-hover'
                                                      : 'rounded bg-blue-600 text-white hover:bg-blue-700'
                                              }`}
                                          >
                                              Apply
                                          </button>
                                      </div>
                                  </div>,
                                  document.body
                              )}
                      </>
                  ) : (
                      <ProfileTableSettingsBar onOpen={() => setSettingsOpen(true)} />
                  ))}
            <div className={variantStyles.container}>
                {loading ? (
                    <div className="flex items-center justify-center py-8 w-full">
                        <span className={`text-sm ${mutedTextClass}`}>Loading...</span>
                    </div>
                ) : data.length === 0 ? (
                    emptyState ? (
                        <div className="w-full">{emptyState}</div>
                    ) : (
                        <div className="text-center py-6 w-full">
                            <span className={`text-sm ${mutedTextClass}`}>{emptyMessage}</span>
                        </div>
                    )
                ) : (
                    <RowDndProvider
                        enabled={!!rowDnd}
                        sensors={rowSensors}
                        onDragStart={handleRowDragStart}
                        onDragEnd={handleRowDragEnd}
                        onDragCancel={() => setActiveRowId(null)}
                        overlay={activeRowId != null ? rowDnd?.renderDragOverlay?.(activeRowId) : null}
                    >
                        <div className={variantStyles.scroll} ref={scrollContainerRef}>
                            {/*
                             * `w-full` (width: 100%) guarantees the table spans the container,
                             * so when the sum of fixed column widths is less than the available
                             * space, browsers distribute the remainder across columns (with
                             * `table-layout: auto`) rather than leaving an empty band on the
                             * right. Replaces the previous `min-w-full` which only set a floor
                             * but didn't force the table to fill wider containers.
                             *
                             * When `virtualizeRows` is set we switch to `table-layout: fixed`
                             * so the visible window of rows can't change column widths as the
                             * user scrolls — column widths are then governed by the header row
                             * (which is always rendered) instead of arbitrary row content.
                             */}
                            <table
                                className={`${variantStyles.table} ${tableClassName ?? ''}`.trim()}
                                style={{ tableLayout: virtualizeRows ? 'fixed' : 'auto' }}
                            >
                                <thead className={variantStyles.thead}>
                                    <ProfileTableHeaderRow<TFixedKey, TMiddleKey>
                                        uiVariant={uiVariant}
                                        sensors={sensors}
                                        visibleColumns={typedVisibleColumns}
                                        fixedColumns={fixedColumns}
                                        sortableItems={sortableItems}
                                        sortableColumns={sortableColumns}
                                        activeSortColumn={activeSortColumn}
                                        sortDirection={sortDirection}
                                        fixedColumnKeys={fixedColumnKeys}
                                        addableColumns={addableColumns}
                                        visibleHideableColumnsCount={visibleHideableColumnsCount}
                                        activeColumnItem={activeColumnItem as ProfileColumnDef<TFixedKey | TMiddleKey> | null}
                                        resizingColumn={resizingColumn}
                                        allSelected={allSelected}
                                        someSelected={someSelected}
                                        accentColor={accentColor}
                                        hoverAccent={hoverAccent}
                                        getHeaderCellStyle={getHeaderCellStyle}
                                        getColumnWidth={getColumnWidth as (key: TFixedKey | TMiddleKey) => number}
                                        isColumnCollapsed={isColumnCollapsed as (key: TFixedKey | TMiddleKey) => boolean}
                                        toggleCollapse={toggleCollapse}
                                        hideColumn={hideColumn}
                                        addColumnToRight={addColumnToRight}
                                        onSelectAll={onSelectAll}
                                        onSortChange={onSortChange}
                                        selectionSort={selectionSort}
                                        fixedColumnSorts={fixedColumnSorts}
                                        getStickyOffset={getStickyOffset}
                                        handleResizeStart={handleResizeStart}
                                        handleDragStart={handleDragStart}
                                        handleDragOver={handleDragOver}
                                        handleDragEnd={handleDragEnd}
                                        clearDragState={clearDragState}
                                        selectionColumnKey={selectionColumnKey === undefined ? fixedColumns[0]?.key : (selectionColumnKey ?? undefined)}
                                    />
                                </thead>
                                <tbody>
                                    <MaybeSortableContext enabled={!!rowDnd} items={effectiveRowIds}>
                                        {/*
                                         * Spacer rows preserve total scroll height when
                                         * virtualization is on. They have no content and
                                         * are aria-hidden so screen readers ignore them.
                                         */}
                                        {virtualWindow.paddingTop > 0 && (
                                            <tr aria-hidden="true" style={{ height: virtualWindow.paddingTop }}>
                                                <td colSpan={typedVisibleColumns.length} style={{ padding: 0, border: 0 }} />
                                            </tr>
                                        )}
                                        {(rowDnd ? orderedData : data.slice(virtualWindow.startIndex, virtualWindow.endIndex)).map((row, sliceIndex) => {
                                            const index = rowDnd ? sliceIndex : virtualWindow.startIndex + sliceIndex
                                            const rowKey = getRowKey(row, index)
                                            const isExpanded = expandedKey === rowKey
                                            const searchId = getRowSearchId?.(row, index)
                                            const rowClassName = variantStyles.row
                                            const onRowClickHandler = () => (onRowClick ? onRowClick(rowKey, row) : onToggleExpand?.(rowKey))
                                            const renderCells = (dragHandle?: RowDragHandleProps) =>
                                                renderRow({
                                                    row,
                                                    index,
                                                    columns: typedVisibleColumns,
                                                    getColumnWidth: getColumnWidth as (key: TFixedKey | TMiddleKey) => number,
                                                    getCellStyle,
                                                    getStickyOffset,
                                                    isColumnCollapsed: isColumnCollapsed as (key: TFixedKey | TMiddleKey) => boolean,
                                                    resizingColumn: resizingColumn as (TFixedKey | TMiddleKey) | null,
                                                    dragHandle,
                                                })
                                            const sortableId = rowDnd?.getSortableId(row) ?? null
                                            return (
                                                <React.Fragment key={rowKey}>
                                                    {rowDnd && sortableId != null ? (
                                                        <SortableRow
                                                            id={sortableId}
                                                            disabled={!!rowDnd.disabled || rowReordering}
                                                            className={rowClassName}
                                                            searchId={searchId}
                                                            onClick={onRowClickHandler}
                                                            renderCells={renderCells}
                                                        />
                                                    ) : (
                                                        <tr data-search-id={searchId} className={rowClassName} onClick={onRowClickHandler}>
                                                            {renderCells()}
                                                        </tr>
                                                    )}
                                                    {isExpanded && renderExpanded && (
                                                        <tr>
                                                            <td colSpan={typedVisibleColumns.length} className={variantStyles.expanded}>
                                                                {renderExpanded(row)}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}
                                        {virtualWindow.paddingBottom > 0 && (
                                            <tr aria-hidden="true" style={{ height: virtualWindow.paddingBottom }}>
                                                <td colSpan={typedVisibleColumns.length} style={{ padding: 0, border: 0 }} />
                                            </tr>
                                        )}
                                    </MaybeSortableContext>
                                </tbody>
                            </table>
                        </div>
                    </RowDndProvider>
                )}
            </div>
            {pagination && onPaginationChange && (
                <TablePagination
                    page={pagination.page}
                    setPage={(page) => onPaginationChange({ ...pagination, page })}
                    rowsPerPage={pagination.limit}
                    setRowsPerPage={(limit) => onPaginationChange({ ...pagination, limit, page: 1 })}
                    total={pagination.total}
                />
            )}
            {showSettingsProp && settingsVariant === 'modal' && (
                <TableSettings
                    open={settingsOpen}
                    onClose={() => setSettingsOpen(false)}
                    title="Table Settings"
                    columns={settingsColumns}
                    visibleColumns={settingsVisibleMap}
                    onColumnChange={handleSettingsColumnChange}
                    columnOrder={settingsColumnOrder}
                    onColumnOrderChange={handleSettingsOrderChange}
                />
            )}
        </div>
    )
}

export type { ProfileColumnDef, RenderRowProps, RowDragHandleProps } from './types'
